import type { PlanTier } from './auth';

// ─── Plan pricing (AED) — single source of truth ─────────────────────────────
// Consumed by: apps/dashboard billing page (display + checkout), apps/web
// pricing page, and the admin portal's MRR estimation. Keep all surfaces on
// THIS map — never hardcode plan prices in app code.

export interface PlanPricing {
  /**
   * AED per month, monthly billing.
   * 0 when the plan is `yearlyOnly` (no monthly option exists — never display it).
   */
  monthlyAed: number;
  /** AED per year, annual billing (already discounted) */
  yearlyAed: number;
  /**
   * Plan is sold on a yearly subscription ONLY — no monthly cycle exists.
   * Surfaces must show the yearly total (no monthly figure at all) and checkout
   * must reject `cycle: 'monthly'` (`PLAN_CYCLE_NOT_AVAILABLE`).
   */
  yearlyOnly?: boolean;
}

export const PLAN_PRICING_AED: Record<PlanTier, PlanPricing> = {
  free:       { monthlyAed: 0,     yearlyAed: 0 },
  // Founder decision D1 (2026-07-10): Starter is yearly-only at AED 990 TOTAL/year.
  // Existing starter-monthly subscribers are grandfathered at the webhook layer
  // (old Stripe price IDs stay recognized) — only NEW monthly checkout is blocked.
  starter:    { monthlyAed: 0,     yearlyAed: 990, yearlyOnly: true },
  menu:       { monthlyAed: 549,   yearlyAed: 5_270.40 },
  operate:    { monthlyAed: 799,   yearlyAed: 7_670.40 },
  scale:      { monthlyAed: 1_199, yearlyAed: 11_510.40 },
  // Priced at Scale for launch (founder, 2026-09-10). One place to change when the hotel
  // pricing is settled — nothing else reads a number.
  hotel:      { monthlyAed: 1_199, yearlyAed: 11_510.40 },
  command:    { monthlyAed: 1_699, yearlyAed: 16_310.40 },
  control:    { monthlyAed: 549,   yearlyAed: 5_270.40 },   // legacy alias for menu
  enterprise: { monthlyAed: 1_699, yearlyAed: 16_310.40 },  // legacy alias for command
};

// ─── One-time setup & training service (AED) ─────────────────────────────────
// Founder decision D2 (2026-07-10): done-for-you onboarding (menu generation,
// floor plan + tables, QR codes, staff training) charged ONCE on the first
// MONTHLY subscription (menu tier and up). FREE with yearly plans. This is a
// subscription onboarding fee, NOT a transaction fee (those stay banned).
export const SETUP_TRAINING_FEE_AED = 999;

// ─── Recurring add-on pricing (AED / month) ──────────────────────────────────

export const ADDON_MONTHLY_PRICING_AED: Record<string, number> = {
  addon_ai_credits: 49,
  addon_payment: 79,
  addon_staff: 25,       // per seat
  // addon_location is NOT here — its price depends on the account's plan.
  // Use EXTRA_LOCATION_AED_BY_PLAN / getExtraLocationAed instead.
};

export const EXTRA_STAFF_SEAT_AED = 25;

// ─── Extra location pricing (AED / month, per location) ──────────────────────
// Founder decision (2026-07-15): the price of an additional location follows the
// account's CURRENT plan rather than being flat. A plan absent from this map
// cannot buy extra locations at all — notably `command` (Commander), which is
// closed because multiple dashboards under one account don't exist.
//
// MONTHLY ONLY — deliberate, and true of every add-on (staff seats, AI credits,
// payment processing). There is no yearly location price even for accounts on a
// yearly plan: they pay the plan yearly and add-ons monthly, which is already how
// staff seats behave. So the Stripe prices below must be MONTHLY recurring, and
// nothing here needs a cycle dimension.
//
// Each entry needs its own Stripe price; the ids are read from
// STRIPE_PRICE_ID_ADDON_LOCATION_{MENU,OPERATE,SCALE} (see billing.service).

export const EXTRA_LOCATION_AED_BY_PLAN: Partial<Record<PlanTier, number>> = {
  menu:    499,
  control: 499,   // legacy alias for menu
  operate: 650,
  scale:   999,
  // Hotels genuinely run several outlets — a restaurant, a bar, room service — so unlike
  // `command` this plan can buy locations. Priced with Scale, as the plan itself is.
  hotel:   999,
};

/** AED/month for one extra location on `plan`, or null when the plan can't buy any. */
export function getExtraLocationAed(plan: PlanTier): number | null {
  return EXTRA_LOCATION_AED_BY_PLAN[plan] ?? null;
}

/** Whether `plan` may purchase extra locations at all. */
export function planAllowsExtraLocations(plan: PlanTier): boolean {
  return getExtraLocationAed(plan) !== null;
}

/**
 * Point-in-time MRR estimate for one paying org (AED / month).
 * NOTE: estimate — annual subscriptions are normalized via the discounted
 * yearly price / 12; true interval lives in Stripe.
 */
export function estimateOrgMrrAed(params: {
  plan: PlanTier;
  extraStaffCount?: number;
  extraLocationCount?: number;
  activeAddons?: readonly string[] | null;
}): number {
  // Yearly-only plans have no monthly price — normalize from the yearly total
  // (grandfathered legacy-monthly subs are slightly under-estimated; acceptable
  // for an estimate, far better than counting them as 0).
  const pricing = PLAN_PRICING_AED[params.plan];
  const base = pricing ? (pricing.yearlyOnly ? pricing.yearlyAed / 12 : pricing.monthlyAed) : 0;
  const staff = (params.extraStaffCount ?? 0) * EXTRA_STAFF_SEAT_AED;
  // Location price follows the plan. A plan that can't buy locations contributes 0
  // even if a stale extra_location_count lingers from before a downgrade.
  const locations = (params.extraLocationCount ?? 0) * (getExtraLocationAed(params.plan) ?? 0);
  const addons = (params.activeAddons ?? []).reduce(
    // addon_staff / addon_location quantities are billed via the seat counts
    // above — don't double-count them from the addons list
    (sum, id) => (id === 'addon_staff' || id === 'addon_location' ? sum : sum + (ADDON_MONTHLY_PRICING_AED[id] ?? 0)),
    0,
  );
  return base + staff + locations + addons;
}
